/* Equestrian page — small, dependency-free interactivity:
 *   1. Testimonial slider auto-rotation + dot/arrow controls
 *   2. Video slider — halio-sonic style center-focus, click-to-play, looping.
 *      No autoplay, no preload: videos cost 0 bytes until the user clicks
 *      the play overlay on the active card.
 */

(function () {
  if (typeof document === 'undefined') return;

  // -------------------------------------------------------------------------
  // Testimonial slider
  // -------------------------------------------------------------------------

  function initTestimonialSliders() {
    document.querySelectorAll('[data-eq-testi-slider]').forEach((root) => {
      const slides = root.querySelectorAll('[data-eq-testi-slide]');
      const dots = root.querySelectorAll('[data-eq-testi-dot]');
      const prev = root.querySelector('[data-eq-testi-prev]');
      const next = root.querySelector('[data-eq-testi-next]');
      if (slides.length <= 1) return;

      const autoSlide = root.getAttribute('data-auto-slide') === 'true';
      const interval = parseInt(root.getAttribute('data-interval'), 10) || 6000;
      let i = 0;
      let timer = null;

      function show(n) {
        i = ((n % slides.length) + slides.length) % slides.length;
        slides.forEach((s, idx) => {
          s.setAttribute('aria-hidden', idx === i ? 'false' : 'true');
        });
        dots.forEach((d, idx) => {
          d.classList.toggle('is-active', idx === i);
        });
      }
      function nextSlide() { show(i + 1); }
      function prevSlide() { show(i - 1); }
      function start() {
        if (!autoSlide) return;
        stop();
        timer = window.setInterval(nextSlide, interval);
      }
      function stop() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }

      prev && prev.addEventListener('click', () => { prevSlide(); start(); });
      next && next.addEventListener('click', () => { nextSlide(); start(); });
      dots.forEach((d, idx) => {
        d.addEventListener('click', () => { show(idx); start(); });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);

      show(0);
      start();
    });
  }

  // -------------------------------------------------------------------------
  // Video slider — center-focused, click-to-play, infinite loop.
  //
  // To get continuous left + right neighbors at every logical position
  // (including the first and last original slide), we triple the slide set
  // in the DOM (3 copies of the originals) and always operate inside the
  // MIDDLE copy. When the user navigates past the middle copy's bounds, we
  // silently snap the track back to the equivalent position in the middle
  // copy after the animation finishes — the swap is invisible because the
  // visual remains the same image (it's a clone of the same original).
  //
  // Videos use `preload="none"` so nothing is fetched until the user clicks
  // the play button on the active card. Clones don't add network cost.
  // -------------------------------------------------------------------------

  function debounce(fn, ms) {
    let t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function initVideoSliders() {
    document.querySelectorAll('[data-eq-vslider]').forEach((root) => {
      const track = root.querySelector('[data-eq-vslider-track]');
      const originals = Array.from(root.querySelectorAll('[data-eq-vslider-slide]'));
      const N = originals.length;
      if (N === 0 || !track) return;
      const prev = root.querySelector('[data-eq-vslider-prev]');
      const next = root.querySelector('[data-eq-vslider-next]');

      // Triple the slides for looping. Skip if only one slide (no loop needed).
      if (N >= 2) {
        for (let pass = 0; pass < 2; pass++) {
          originals.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.setAttribute('data-clone', 'true');
            track.appendChild(clone);
          });
        }
      }

      const slides = Array.from(track.querySelectorAll('[data-eq-vslider-slide]'));
      const totalSlides = slides.length; // either N or 3N
      const middleStart = N >= 2 ? N : 0;     // first index in the middle copy
      const middleEnd = N >= 2 ? 2 * N : 0;   // first index AFTER the middle copy
      let active = middleStart;               // DOM index, always inside middle copy on render

      function pauseAll() {
        slides.forEach((s) => {
          const v = s.querySelector('[data-eq-vslider-video]');
          if (v && !v.paused) {
            v.pause();
            try { v.currentTime = 0; } catch (e) {}
          }
          s.classList.remove('is-playing');
        });
      }

      function applyTransform() {
        const card = slides[active];
        if (!card) return;
        // Browsers sometimes scroll an overflow-clipped container when a
        // descendant gets focus. Reset so our translateX math anchors to a
        // stable origin every time.
        if (root.scrollLeft !== 0) root.scrollLeft = 0;
        const viewportW = root.getBoundingClientRect().width;
        const cardW = card.offsetWidth;
        const cardCenter = card.offsetLeft + cardW / 2;
        const offset = viewportW / 2 - cardCenter;
        track.style.transform = 'translateX(' + offset + 'px)';
      }

      function render() {
        slides.forEach((s, i) => {
          const isActive = i === active;
          s.setAttribute('aria-current', isActive ? 'true' : 'false');
          const playBtn = s.querySelector('[data-eq-vslider-play]');
          if (playBtn) playBtn.tabIndex = isActive ? 0 : -1;
        });
        applyTransform();
      }

      // After an animated move, if we've left the middle copy, silently jump
      // back to the equivalent slot in the middle copy. The user never sees
      // the swap because the slide at the destination is a clone of the same
      // original they were viewing.
      function snapBackIfNeeded() {
        if (N < 2) return;
        if (active >= middleStart && active < middleEnd) return;
        const original = ((active % N) + N) % N;
        const target = middleStart + original;
        const previousTransition = track.style.transition;
        track.style.transition = 'none';
        active = target;
        applyTransform();
        // Force a reflow before restoring the transition so the snap is not animated.
        // eslint-disable-next-line no-unused-expressions
        track.offsetHeight;
        track.style.transition = previousTransition;
        render();
      }

      function go(delta) {
        pauseAll();
        active += delta;
        render();
      }

      function goToDomIndex(i) {
        if (i === active) return;
        pauseAll();
        active = i;
        render();
      }

      function playSlide(slide) {
        const video = slide.querySelector('[data-eq-vslider-video]');
        if (!video) return;
        pauseAll();
        slide.classList.add('is-playing');
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => slide.classList.remove('is-playing'));
        }
      }

      slides.forEach((slide, i) => {
        slide.addEventListener('click', (e) => {
          const playBtn = e.target.closest('[data-eq-vslider-play]');
          if (playBtn) {
            if (i === active) {
              e.preventDefault();
              playSlide(slide);
            }
            return;
          }
          if (i !== active) goToDomIndex(i);
        });
        const video = slide.querySelector('[data-eq-vslider-video]');
        if (video) {
          video.addEventListener('ended', () => { slide.classList.remove('is-playing'); });
          video.addEventListener('pause', () => { slide.classList.remove('is-playing'); });
        }
      });

      prev && prev.addEventListener('click', () => go(-1));
      next && next.addEventListener('click', () => go(1));

      track.addEventListener('transitionend', (e) => {
        // Only react when the transform itself finishes (avoid filter/opacity).
        if (e.propertyName !== 'transform') return;
        snapBackIfNeeded();
      });

      // Touch swipe — single-finger horizontal swipe changes active card
      let touchStartX = null;
      let touchStartY = null;
      track.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      track.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          go(dx < 0 ? 1 : -1);
        }
        touchStartX = touchStartY = null;
      }, { passive: true });

      window.addEventListener('resize', debounce(applyTransform, 120));
      window.addEventListener('load', () => requestAnimationFrame(applyTransform));

      render();
    });
  }

  // -------------------------------------------------------------------------
  // Product image carousel — <eq-carousel> custom element.
  // Vanilla, no swipe library. Behaviors:
  //   - prev/next + dots switch slides (translate3d on the track)
  //   - left/right arrow keys when focus is inside the carousel
  //   - touch swipe (threshold 40px or 15% of viewport width)
  //   - aria-selected on dots + aria-hidden on inactive slides update on change
  //   - no auto-advance (product imagery should be user-driven)
  // -------------------------------------------------------------------------

  function defineCarousel() {
    if (typeof customElements === 'undefined') return;
    if (customElements.get('eq-carousel')) return;

    class EqCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-eq-carousel-track]');
        this.slides = Array.from(this.querySelectorAll('.eq-carousel__slide'));
        this.prev = this.querySelector('[data-eq-carousel-prev]');
        this.next = this.querySelector('[data-eq-carousel-next]');
        this.dots = Array.from(this.querySelectorAll('[data-eq-carousel-dot]'));
        this.viewport = this.querySelector('.eq-carousel__viewport');
        if (!this.track || this.slides.length === 0) return;

        this.index = 0;
        this.count = this.slides.length;

        if (this.count <= 1) {
          this.update();
          return;
        }

        this.prev && this.prev.addEventListener('click', () => this.go(this.index - 1));
        this.next && this.next.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        });

        // Touch swipe
        let startX = null, deltaX = 0;
        this.viewport.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          deltaX = 0;
        }, { passive: true });
        this.viewport.addEventListener('touchmove', (e) => {
          if (startX === null) return;
          deltaX = e.touches[0].clientX - startX;
        }, { passive: true });
        this.viewport.addEventListener('touchend', () => {
          if (startX === null) return;
          const w = this.viewport.getBoundingClientRect().width || 1;
          const threshold = Math.max(40, w * 0.15);
          if (Math.abs(deltaX) > threshold) {
            this.go(this.index + (deltaX < 0 ? 1 : -1));
          }
          startX = null;
          deltaX = 0;
        });

        this.update();
      }

      go(n) {
        // Clamp (no wrap — at the ends, button just stays disabled).
        const i = Math.max(0, Math.min(this.count - 1, n));
        if (i === this.index) return;
        this.index = i;
        this.update();
      }

      update() {
        if (this.track) {
          this.track.style.transform = 'translate3d(' + (-this.index * 100) + '%, 0, 0)';
        }
        this.slides.forEach((s, i) => {
          s.toggleAttribute('aria-hidden', i !== this.index);
        });
        this.dots.forEach((d, i) => {
          const active = i === this.index;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (this.prev) this.prev.toggleAttribute('disabled', this.index === 0);
        if (this.next) this.next.toggleAttribute('disabled', this.index === this.count - 1);
      }
    }

    customElements.define('eq-carousel', EqCarousel);
  }

  function init() {
    initTestimonialSliders();
    initVideoSliders();
    defineCarousel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
